<template>
  <Layout>
    <PageHeader
      :title="$t('setting.list.notification.title')"
      :items="breadcrumbItems"
      hasBreadcrumb
      hasBackButton
    >
      <template #afterTitle>
        <div class="d-flex gap-2">
          <McButton
            variant="secondary"
            icon="icon-outline_history"
            :text="$t('setting.list.notification.form.changeHistoryList')"
            @click="openChangeLog"
          />

          <McButton
            v-if="hasAccess(['e4b-alert-center-rule-setting:create'])"
            variant="primary"
            icon="icon-fill_plus"
            :text="$t('setting.list.notification.add')"
            @click="
              $router.push({
                name: 'Add Setting Notification',
              })
            "
          />
        </div>
      </template>
    </PageHeader>

    <div class="table-responsive my-3">
      <McGrapqlTable
        ref="notificationTable"
        :apiClient="query"
        :fields="fields"
        :hasCheckbox="true"
        :searchable="true"
        :withCard="true"
        :isSelectableField="false"
        :hasDelete="false"
        trackBy="alertRuleId"
        :filterBottom="true"
        :customHandlers="customHandlers"
        :refreshData="isLoading"
        :deleteObject="$t('setting.list.notification.rule')"
        :defaultLimit="defaultLimit"
        :bulkDeleteApi="true"
      >
        <template
          v-if="
            hasAccessAll([
              'e4b-alert-center-rule-setting:update',
              'e4b-alert-center-rule-setting:delete',
            ])
          "
          #button-filter="{ showDeleteModal, selectedIds, isSelected }"
        >
          <div class="d-flex justify-content-end me-0">
            <div class="me-2">
              <b-dropdown
                id="action-dropdown"
                right
                variant="outline-primary"
                menu-class="dropdown-menu-end"
                :disabled="isSelected == 0"
              >
                <template #button-content>
                  {{ $t('global.field.action') }} <i class="mc-fa icon-outline_chevron-down"></i>
                </template>
                <b-dropdown-item href="#" @click="openModalUpdate(selectedIds, true)">
                  <span>
                    {{ $t('setting.list.notification.activate') }}
                  </span>
                </b-dropdown-item>
                <b-dropdown-item href="#" @click="openModalUpdate(selectedIds, false)">
                  <span>
                    {{ $t('setting.list.notification.deactivate') }}
                  </span>
                </b-dropdown-item>
                <b-dropdown-item href="#" @click="showDeleteModal()">
                  <span class="text-danger">
                    {{ $t('global.delete') }}
                  </span>
                </b-dropdown-item>
              </b-dropdown>
            </div>

            <ModalFilter
              :activeFilters="filterModal"
              :alertCode="getAlertOptions"
              @applyFilters="handleApplyFilters"
              @applyFullObjects="handleLocalFullObjects"
            />
          </div>
        </template>

        <template #cell(label)="data">
          <div class="d-flex justify-content-between">
            <div
              class="fw-bold text-primary td__noellipsis clickable-field"
              @click="customHandlers.handleClickRow(data.item)"
            >
              {{ data.value }}
              <p class="fw-normal text-muted">
                {{ lang == 'en' ? data.item.alert.descriptionEng : data.item.alert.descriptionIdn }}
                <template v-if="data.item.alertCode == 8">
                  <template v-if="hasAccess('e4c-alert-overspeed-threshold:read')">
                    {{
                      data.item.maxThreshold
                        ? `&middot; ${data.item.maxThreshold} Km/${$t('global.time.hour')}`
                        : ''
                    }}
                  </template>
                  <template v-if="hasAccess('e4d-alert-overspeed-threshold-duration:read')">
                    {{
                      data.item.minThresholdTime !== null && data.item.minThresholdTime !== 0
                        ? `&middot; ${data.item.minThresholdTime} ${$t('global.time.seconds')}`
                        : '&middot; Instant'
                    }}
                  </template>
                </template>
              </p>
            </div>
            <div class="mc-alert-rule_label-icon">
              <img
                v-if="data.item.alertCountThreshold && data.item.alertCountWindow"
                v-b-tooltip.hover
                src="@/assets/images/notification/intensity.png"
                alt="intensity"
                :title="`${data.item.alertCountThreshold}x ${$t('global.in')} ${
                  data.item.alertCountWindow
                } ${$t('global.time.minutes')}`"
              />
              <i
                v-if="
                  data.item.alertUrgencyType == 'Urgent' &&
                  hasAccess(['e4e-alert-center-urgent:read'])
                "
                v-b-tooltip.hover
                class="mc-icon icon-fill_alert-triangle"
                :title="$t('setting.list.notification.urgentSituation')"
              >
              </i>
            </div>
          </div>
        </template>

        <template #cell(userIsSubscribedToRule)="data">
          <span :class="data.value ? 'mc-user-include_green' : 'mc-user-include_red'">
            <i v-if="data.value" class="mc-fa icon-fill_checkmark-circle" />
            <i v-if="!data.value" class="mc-fa icon-outline_close-circle" />
          </span>
        </template>

        <template #cell(isActive)="data">
          <span :class="data.value ? 'mc-status mc-status--green' : 'mc-status mc-status--red'">
            {{
              data.value
                ? $t('setting.list.notification.active')
                : $t('setting.list.notification.nonActive')
            }}
          </span>
        </template>

        <template #cell(createdOn)="data">
          <div>
            <strong>
              {{ momentDateShortMonth(data.value) }}
            </strong>
          </div>
          <div class="mc-datetime">
            {{ momentTimeSeconds(data.value) }} {{ getLabelTimezone() }}
          </div>
        </template>

        <template #cell(totalVehicles)="data">
          <span v-if="data.item.vgroups.length > 0">
            {{ data.item.vgroups.length }} {{ $t('global.object.fleetGroup') }}
          </span>
          <span v-else-if="data.value == 0">
            {{ $t('setting.list.token.allFleet') }}
          </span>
          <span v-else-if="data.value > 0"> {{ data.value }} {{ $t('global.object.fleet') }} </span>
        </template>
      </McGrapqlTable>

      <b-modal
        id="modal-update-notification"
        ref="modal-update-notification"
        class="mc-modal"
        :title="`${
          status
            ? $t('setting.list.notification.activate')
            : $t('setting.list.notification.deactivate')
        } ${$t('setting.list.notification.rule')}`"
        size="md"
        centered
        scrollable
      >
        <div class="card-body">
          {{
            $t('setting.list.notification.ruleChange', {
              status: status
                ? $t('setting.list.notification.activate').toLowerCase()
                : $t('setting.list.notification.deactivate').toLowerCase(),
            })
          }}
        </div>
        <template #modal-footer="{ cancel }">
          <McButton variant="secondary" :text="$t('global.cancel')" @click="cancel()" />
          <McButton
            variant="primary"
            :text="`${$t('global.yes')}, ${
              status
                ? $t('setting.list.notification.activate')
                : $t('setting.list.notification.deactivate')
            }`"
            @click="handleUpdate(selected, status)"
          />
        </template>
      </b-modal>
    </div>
    <ChangeLogList ref="changelogModal" :alert-codes="getAlertOptions" />
  </Layout>
</template>

<script>
import appConfig from '@/app.config'
import { mapGetters } from 'vuex'
import Layout from '@/router/layouts/main'
import PageHeader from '@/components/page-header-v2.vue'
import McButton from '@/components/McButton'
import McGrapqlTable from '@/components/McGrapqlTable'
import i18n from '../../../../i18n'
import {
  GET_NOTIFICATIONS_LIST,
  GET_ACTIVE_NOTIFICATIONS_LIST,
  DELETE_NOTIFICATION_RULES_NEW,
  UPDATE_NOTIFICATION_RULES_NEW,
} from '@/services/api/setting/notification/List'
import { Helper } from '@/mixin/helper'
import { Notification } from '@/mixin/notification'
import ModalFilter from './components/ModalFilter'
import { NOTIFICATION_LIST_CODES } from '@/services/api/notification/general'
import { GET_VEHICLE_FILTER_ALL } from '@/services/api/master/fleet'
import ChangeLogList from './components/ChangeLogList.vue'

export default {
  page: {
    title: 'Setting Notification',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    Layout,
    PageHeader,
    McButton,
    McGrapqlTable,
    ModalFilter,
    ChangeLogList,
  },

  mixins: [Helper, Notification],

  apollo: {
    alertRules: {
      query: GET_ACTIVE_NOTIFICATIONS_LIST,
      fetchPolicy: 'no-cache',
      variables() {
        return {
          filter: {
            pagination: {
              page: 1,
              show: 99999,
            },
          },
        }
      },
      result({ data, loading }) {
        if (!loading) this.allData = data.alertRules.alertRules.data
      },
    },
    alertCodes: {
      query: NOTIFICATION_LIST_CODES,
      fetchPolicy: 'no-cache',
      variables() {
        return {
          filter: {
            alertCode: this.getNotificationAlertCodes(),
          },
        }
      },
      result({ data, loading }) {
        if (loading) return

        const res = [...data.alertCodes.alertCodes]
        this.notifications = res
      },
    },

    vehicles: {
      query: GET_VEHICLE_FILTER_ALL,
      variables() {
        return {
          filter: {
            status: ['I', 'R', 'A'],
            orderBy: JSON.stringify({ licensePlate: 'asc' }),
          },
        }
      },
      result({ data, loading }) {
        if (loading) return

        this.vehicles = data.vehicles.vehicles
      },
    },
  },

  data() {
    return {
      customHandlers: {
        handleClickRow: (item) => {
          this.$router.push({
            name: 'Detail Setting Notification',
            params: {
              id: item.alertRuleId,
            },
          })
        },
      },
      lang: i18n.locale,
      isLoading: false,
      allData: [],
      dataSelected: [],
      status: true,
      selected: [],
      defaultLimit: 25,
      notifications: [],
      vehicles: [],
      filterModal: {},
      localFullObjects: [],
      rawFilters: {},
      activeFilters: {},
    }
  },

  computed: {
    ...mapGetters({
      hasAccess: 'identity/hasAccess',
      hasAccessAll: 'identity/hasAccessAll',
    }),

    query() {
      return {
        get: async (filter) => {
          let modalFilters = {}
          if (this.filterModal) {
            const filters = this.filterModal || {}
            if (filters.alertCode?.length > 0) modalFilters.alertCode = filters.alertCode
            if (filters.vehicleId && filters.vehicleId?.length > 0)
              modalFilters.vehicleId = filters.vehicleId

            if (filters.category) {
              const isIntensity = filters.category.intensity ? [true] : [false]
              if (filters.category.intensity) modalFilters.isIntensity = isIntensity

              const alertUrgencyType = Object.entries(filters.category)
                .filter(([key, value]) => key !== 'intensity' && value === true)
                .map(([key]) => key[0].toUpperCase() + key.slice(1))

              if (alertUrgencyType.length > 0) modalFilters.alertUrgencyType = alertUrgencyType
            }

            if (filters.statusCheck) {
              if (filters.statusCheck.active !== filters.statusCheck.inactive)
                modalFilters.isActive = filters.statusCheck.active ? true : false
            }

            const createdOn = Array.isArray(filters.createdOn) ? filters.createdOn : [null, null]
            if (createdOn.length == 2) {
              modalFilters.createdAfter = createdOn[0] ? new Date(createdOn[0]) : null
              modalFilters.createdBefore = createdOn[1] ? new Date(createdOn[1]) : null
            }
          }

          const res = await this.$apollo.query({
            query: GET_NOTIFICATIONS_LIST,
            fetchPolicy: 'no-cache',
            variables: {
              filter: {
                ...filter,
                ...modalFilters,
                entityType: ['AlertRule'],
              },
            },
          })

          return res.data.alertRules.alertRules
        },
        delete: async (id) => {
          const res = await this.$apollo.mutate({
            mutation: DELETE_NOTIFICATION_RULES_NEW,
            fetchPolicy: 'no-cache',
            variables: {
              input: {
                alertRuleIds: Array.isArray(id) ? id.map((item) => +item) : [+id],
              },
            },
          })

          return res.data.deleteAlertRules
        },
      }
    },

    flaggedField() {
      if (!this.hasAccess('e4c-alert-overspeed-threshold:read')) {
        return []
      } else {
        return [
          {
            key: 'maxThreshold',
            label: this.$t('setting.list.notification.form.mainRule'),
            isSortable: false,
            thClass: 'mc-table--200',
            formatter: (val) => {
              if (val) return 'Max ' + val + ' Km/' + this.$t('global.time.hour')
              else return '-'
            },
          },
        ]
      }
    },

    fields() {
      return [
        {
          key: 'label',
          label: this.$t('setting.list.notification.form.notificationName'),
          isRedirectable: true,
          isSortable: false,
          thClass: 'mc-table--300',
        },
        {
          key: 'userIsSubscribedToRule',
          label: this.$t('setting.list.notification.acceptNotification'),
          isSortable: false,
          thClass: 'mc-table--150',
        },
        {
          key: 'isActive',
          label: 'Status',
          isSortable: false,
          thClass: 'mc-table--100',
        },
        {
          key: 'createdOn',
          label: this.$t('setting.list.development.createdDate'),
          isSortable: true,
          thClass: 'mc-table--150',
        },
        {
          key: 'totalVehicles',
          label: this.$t('setting.list.notification.form.targetVehicle'),
          isSortable: false,
          thClass: 'mc-table--200',
        },
      ]
    },
    breadcrumbItems() {
      return [
        {
          text: this.$t('setting.homePage.pengaturan'),
          to: '/setting',
        },
        {
          text: this.$t('setting.list.notification.title'),
        },
      ]
    },
  },

  mounted() {
    this.$nextTick(() => {
      if (this.$refs.notificationTable) {
        this.$refs.notificationTable.handleLimitChange(25)
      }
    })
  },

  methods: {
    openModalUpdate(selected, type) {
      this.selected = selected
      this.status = type
      this.$bvModal.show('modal-update-notification')
    },

    handleUpdate(selected, type) {
      const selectedIds = Object.keys(selected).filter((key) => selected[key])

      let temp = []
      selectedIds.forEach((id) => {
        temp.push(this.allData.find((data) => data.alertRuleId == id))
      })

      this.dataSelected = temp

      let check = this.dataSelected.filter((data) => {
        return data.isActive == type
      })

      if (check.length > 0) {
        this.$mcToast.error({
          component: 'McToastDynamicBody',
          props: {
            contents: [
              {
                html: this.$t('setting.list.notification.dataCheck', {
                  status: type
                    ? this.$t('setting.list.notification.nonActive')
                    : this.$t('setting.list.notification.active'),
                }),
              },
            ],
          },
        })
        this.dataSelected = []
        this.$bvModal.hide('modal-update-notification')
        return
      }

      this.$apollo
        .mutate({
          mutation: UPDATE_NOTIFICATION_RULES_NEW,
          variables: {
            input: {
              alertRuleIds: selectedIds.map((id) => +id),
              isActive: type,
            },
          },
        })
        .then((response) => {
          if (response.data.updateAlertRules.isSuccessful) {
            this.isLoading = true
            this.$mcToast.success(this.$t('global.alert.text.changed'))
            this.$bvModal.hide('modal-update-notification')

            localStorage.removeItem('alertRules')

            this.$apollo.queries.alertRules.refetch()
            this.dataSelected = []
            setTimeout(() => {
              this.isLoading = false
            }, 100)
          } else {
            this.$mcToast.error({
              component: 'McToastDynamicBody',
              props: {
                contents: [
                  {
                    html:
                      response.data.updateAlertRules.message ||
                      this.$t('global.alert.text.failedEdit'),
                  },
                ],
              },
            })
          }
        })
        .catch((error) => {
          this.$mcToast.error(this.$t('global.alert.text.failedEdit'))
          // eslint-disable-next-line no-console
          console.error('Error updating notification rules:', error)
          this.dataSelected = []
        })
    },

    handleFilterModal(filters) {
      this.filterModal = {
        alertCode: this.findFilterValue(filters, 'alertCode') || [],
        vehicleId: this.findFilterValue(filters, 'vehicleId') || [],
        createdOn: this.findFilterValue(filters, 'createdOn') || [],
        category: this.findFilterValue(filters, 'category'),
        statusCheck: this.findFilterValue(filters, 'statusCheck'),
      }
    },

    handleChangeActiveFilters(filters) {
      filters.forEach((filter) => {
        this.$delete(this.activeFilters, filter.key)
        if (filter.values === null || filter.values === undefined) return
        if (Array.isArray(filter.values) && filter.values.length === 0) return
        this.$set(this.activeFilters, filter.key, filter.values)
      })
    },
    handleLocalFullObjects(value) {
      this.localFullObjects = value
    },
    async handleApplyFilters(value) {
      this.rawFilters = Array.isArray(value) ? value : []

      this.modalActiveFilters = {}
      this.rawFilters.forEach((filter) => {
        if (filter && filter.key && filter.values !== null && filter.values !== undefined) {
          const modalKey = filter.key // or some inline logic
          if (Array.isArray(filter.values) && filter.values.length > 0) {
            this.modalActiveFilters[modalKey] = filter.values
          } else if (!Array.isArray(filter.values) && filter.values !== '') {
            this.modalActiveFilters[modalKey] = filter.values
          }
        }
      })

      await this.$nextTick()

      this.handleChangeActiveFilters(this.rawFilters)
      this.handleFilterModal(this.rawFilters)

      this.isLoading = true

      setTimeout(() => {
        this.isLoading = false
      }, 1000)
    },

    openChangeLog() {
      this.$refs.changelogModal.show()
    },
  },
}
</script>
